<template>
  <div class="p-6 bg-gray-50 min-h-screen">
    <!-- Encabezado -->
    <h2 class="text-2xl font-bold flex items-center gap-2 mb-6">
      <svg
        class="w-7 h-7 text-amber-600"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h18v18H3V3z" />
      </svg>
      Administrar Productos
    </h2>

    <!-- Panel superior -->
    <div class="bg-white shadow rounded-lg p-4 mb-6 space-y-4">
      <div class="grid md:grid-cols-3 gap-4">
        <input
          v-model.trim="buscar"
          placeholder="🔎 Buscar por nombre..."
          class="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
        />

        <select
          v-model="filtroCategoria"
          class="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todas las categorías</option>
          <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
            {{ cat.nombre }}
          </option>
        </select>

        <label class="flex items-center gap-2 text-sm">
          <input type="checkbox" v-model="soloSinImpuesto" class="w-4 h-4 text-blue-600" />
          Solo productos sin impuesto
        </label>
      </div>

      <div class="grid md:grid-cols-3 gap-4 items-center">
        <select
          v-model="selectedTaxId"
          class="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
        >
          <option disabled value="">Selecciona impuesto</option>
          <option v-for="tax in taxes" :key="tax.id" :value="tax.id">
            {{ tax.nombre }} — {{ Number(tax.porcentaje) }}%
          </option>
        </select>

        <div class="text-xs text-gray-600">
          ⚠️ Las acciones masivas <b>excluyen servicios</b> automáticamente.
          <p v-if="soloSinImpuesto">Aplicado solo a productos sin impuesto.</p>
        </div>

        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 rounded-lg border border-gray-300 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
            :disabled="!selectedTaxId || loading"
            @click="aplicarImpuestoMasivo(true)"
          >
            Impuesto: ON
          </button>
          <button
            class="px-4 py-2 rounded-lg border border-gray-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
            :disabled="loading"
            @click="aplicarImpuestoMasivo(false)"
          >
            Impuesto: OFF
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-sm text-blue-700">
        Procesando… {{ progreso.current }} / {{ progreso.total }} (lote {{ progreso.lote }})
      </div>
    </div>

    <!-- Listado -->
    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <div
        v-for="prod in productosFiltrados"
        :key="prod.id"
        class="bg-white shadow rounded-lg p-4 flex flex-col"
      >
        <div class="flex items-center gap-4 mb-3">
          <img
            :src="prod.imagenUrl || 'https://via.placeholder.com/80'"
            class="w-20 h-20 object-cover rounded"
            alt="Imagen del producto"
          />
          <div class="flex-1">
            <h3 class="text-lg font-semibold flex items-center gap-2">
              {{ prod.nombre }}
              <span
                v-if="prod.tipo === 'servicio'"
                class="text-xs bg-gray-200 text-gray-800 px-2 py-0.5 rounded"
              >
                SERVICIO
              </span>
            </h3>
            <p class="text-sm text-gray-600">
              💵 ${{ (Number(prod.precio) || 0).toFixed(2) }}
              <span
                v-if="prod.tipo !== 'servicio' && (prod.taxIds?.length || prod.taxId)"
                class="text-blue-600 ml-1"
              >
                +impuesto
              </span>
            </p>
            <p class="text-xs text-gray-500">
              🧾
              <span v-if="Array.isArray(prod.taxIds) && prod.taxIds.length">
                IDs: {{ prod.taxIds.join(", ") }}
              </span>
              <span v-else-if="prod.taxId">ID: {{ prod.taxId }}</span>
              <span v-else class="text-rose-600">Sin impuesto</span>
            </p>
            <p class="text-xs text-gray-500">
              🧊 Activo: {{ prod.activo ? "Sí" : "No" }}
            </p>
            <p class="text-xs text-gray-500">
              🧾 POS: {{ prod.mostrarPOS ? "✅" : "❌" }} | 🌐 Web: {{ prod.mostrarWeb ? "✅" : "❌" }}
            </p>
          </div>
        </div>

        <!-- Acciones -->
        <div class="mt-auto space-y-2">
          <button
            @click="editarProducto(prod)"
            class="w-full px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
          >
            ✏️ Editar
          </button>

          <div class="flex gap-2">
            <button
              @click="toggleCampo(prod.id, 'mostrarPOS', !prod.mostrarPOS)"
              class="flex-1 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            >
              POS: {{ prod.mostrarPOS ? "ON" : "OFF" }}
            </button>
            <button
              @click="toggleCampo(prod.id, 'mostrarWeb', !prod.mostrarWeb)"
              class="flex-1 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            >
              Web: {{ prod.mostrarWeb ? "ON" : "OFF" }}
            </button>
            <button
              @click="toggleCampo(prod.id, 'activo', !prod.activo)"
              class="flex-1 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            >
              Activo: {{ prod.activo ? "ON" : "OFF" }}
            </button>
          </div>

          <button
            @click="eliminarProducto(prod.id)"
            class="w-full px-3 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
          >
            🗑 Eliminar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de edición -->
    <div
      v-if="modalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div
        class="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg space-y-4 overflow-y-auto max-h-[90vh]"
      >
        <h3 class="text-xl font-bold mb-4 flex items-center gap-2">
          <svg
            class="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 20h9" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4h9" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 12h9" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 6h.01M3 12h.01M3 18h.01" />
          </svg>
          Editar Producto
        </h3>

        <!-- Campos de edición -->
 <!-- Campos de edición del producto -->
  <input v-model.trim="editado.nombre" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Nombre del producto" />

  <select v-model="editado.categoria" class="w-full p-3 border rounded-lg shadow-sm">
    <option disabled value="">Selecciona categoría</option>
    <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nombre }}</option>
  </select>

  <select v-model="editado.tipo" class="w-full p-3 border rounded-lg shadow-sm">
    <option value="producto">Producto</option>
    <option value="servicio">Servicio</option>
  </select>

  <div class="grid grid-cols-2 gap-3">
    <input v-model.number="editado.costo" type="number" min="0" step="0.01" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Costo unitario ($)" />
    <input v-model.number="editado.margen" type="number" min="0" step="0.01" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Margen (%)" />
  </div>

  <input
    disabled
    :value="((Number(editado.costo) || 0) + ((Number(editado.costo) || 0) * (Number(editado.margen) || 0)) / 100).toFixed(2)"
    class="w-full p-3 border rounded-lg bg-gray-100 text-gray-700 shadow-sm"
  />

  <div v-if="editado.tipo === 'servicio'">
    <input v-model.number="editado.duracion" type="number" min="0" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Duración (min)" />
  </div>

  <div class="grid grid-cols-2 gap-3" v-if="editado.tipo === 'producto'">
    <input v-model.number="editado.stock" type="number" min="0" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Inventario" />
    <input v-model.number="editado.stockMinimo" type="number" min="0" class="w-full p-3 border rounded-lg shadow-sm" placeholder="Stock mínimo" />
  </div>

  <!-- ✅ Impuestos (taxes) -->
  <div v-if="editado.tipo === 'producto'">
    <select v-model="editado.taxId" class="w-full p-3 border rounded-lg shadow-sm">
      <option disabled value="">Selecciona impuesto</option>
      <option v-for="tax in taxes" :key="tax.id" :value="tax.id">
        {{ tax.nombre }} - {{ Number(tax.porcentaje) }}%
      </option>
    </select>
  </div>

  <!-- ✅ Variantes del producto -->
  <div class="space-y-2 border rounded p-3 mt-4">
    <label class="flex items-center gap-2 text-sm">
      <input type="checkbox" v-model="editado.usaVariantes" />
      <span>¿Este producto tiene variantes?</span>
    </label>

    <div v-if="editado.usaVariantes" class="space-y-2">
      <div
        v-for="(v, i) in variantesEdicion"
        :key="v.id || `new-${i}`"
        class="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center border rounded p-2"
      >
        <input
          v-model="v.atributo"
          class="sm:col-span-3 p-2 border rounded text-sm"
          placeholder="Atributo (Color, Talla)"
        />
        <input
          v-model="v.valor"
          class="sm:col-span-3 p-2 border rounded text-sm"
          placeholder="Valor (Azul, M)"
        />
        <input
          v-model="v.descripcion"
          class="sm:col-span-3 p-2 border rounded text-sm"
          placeholder="Descripción (opcional)"
        />
        <input
          v-model.number="v.precio"
          type="number"
          step="0.01"
          class="sm:col-span-2 p-2 border rounded text-sm"
          placeholder="Precio opcional"
        />
        <div class="sm:col-span-1 flex justify-end gap-2">
          <button
            class="px-3 py-1 text-xs rounded bg-red-50 text-red-600 border border-red-200"
            @click="eliminarVariante(i)"
          >
            Eliminar
          </button>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <button
          class="px-3 py-2 rounded bg-blue-600 text-white text-sm font-semibold"
          @click="agregarVarianteFila"
        >
          + Añadir variante
        </button>
        <button
          class="px-3 py-2 rounded bg-green-600 text-white text-sm font-semibold"
          @click="guardarVariantes"
        >
          Guardar variantes
        </button>
      </div>
    </div>
  </div>

  <select v-model="editado.mostrarEn" class="w-full p-3 border rounded-lg shadow-sm">
    <option value="pos">Solo POS</option>
    <option value="ecommerce">Solo eCommerce</option>
    <option value="ambos">POS y eCommerce</option>
  </select>

  <label class="flex items-center gap-2 text-sm">
    <input type="checkbox" v-model="editado.visible" class="w-4 h-4 text-blue-600" />
    Mostrar al público
  </label>

  <div class="space-y-3">
    <img :src="editado.imagenUrl || 'https://via.placeholder.com/150'" class="w-24 h-24 object-cover rounded" alt="Previsualización" />
    <input type="file" accept="image/*" @change="onEditFileChange" class="w-full" />
  </div>

  <!-- Galería eCommerce -->
  <div class="space-y-2">
    <h4 class="text-sm font-semibold text-blue-600 mt-4">Galería eCommerce</h4>
    <div v-if="galeriaActual.length" class="grid grid-cols-4 gap-2">
      <div v-for="(img, idx) in galeriaActual" :key="idx" class="relative group">
        <img :src="img" class="w-full h-20 object-cover rounded border" />
        <button
          class="absolute top-1 right-1 bg-red-600 text-white text-xs rounded px-1 opacity-0 group-hover:opacity-100"
          @click="eliminarFotoGaleria(idx)"
        >
          ✕
        </button>
      </div>
    </div>
    <input type="file" multiple accept="image/*" @change="onEditGaleriaChange" class="w-full" />
    <p class="text-xs text-gray-500">Sube nuevas fotos para reemplazar/actualizar la galería.</p>
  </div>

  <div class="flex justify-between mt-4">
    <button
      @click="guardarCambios"
      class="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 shadow"
    >
      Guardar
    </button>
    <button
      @click="modalVisible = false"
      class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
    >
      Cancelar
    </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from "vue";
import { db, storage } from "@/firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  addDoc,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

// ====== Estado
const productos = ref([]);
const categorias = ref([]);
const taxes = ref([]);

const buscar = ref("");
const filtroCategoria = ref("");
const soloSinImpuesto = ref(true);
const selectedTaxId = ref("");

const loading = ref(false);
const progreso = ref({ current: 0, total: 0, lote: 0 });

// ====== Carga inicial
const cargarProductos = async () => {
  const snap = await getDocs(collection(db, "productos"));
  const arr = snap.docs.map((d) => {
    const data = d.data();

    // Derivar flags desde mostrarEn si existe; mantener compat
    const opt = (data.mostrarEn || "").toLowerCase();
    const mostrarPOS = opt
      ? opt === "pos" || opt === "ambos"
      : !!data.mostrarPOS;
    const mostrarWeb = opt
      ? opt === "ecommerce" || opt === "ambos"
      : !!data.mostrarWeb;

    return {
      id: d.id,
      ...data,
      mostrarPOS,
      mostrarWeb,
      activo: data.activo ?? true,
    };
  });
  // quitar duplicados si existieran docs repetidos
  const m = new Map();
  for (const p of arr) {
    if (!m.has(p.id)) m.set(p.id, p);
  }
  productos.value = Array.from(m.values());
};

const cargarCategorias = async () => {
  const snap = await getDocs(collection(db, "categorias"));
  categorias.value = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

const cargarImpuestos = async () => {
  const snap = await getDocs(collection(db, "impuestos"));
  taxes.value = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

onMounted(async () => {
  await Promise.all([cargarProductos(), cargarCategorias(), cargarImpuestos()]);
});

// ====== Derivados / Filtros
const productosFiltrados = computed(() => {
  const q = buscar.value.trim().toLowerCase();
  return productos.value.filter((p) => {
    // filtro por categoría
    if (filtroCategoria.value && p.categoria !== filtroCategoria.value)
      return false;
    // filtro por texto
    if (
      q &&
      !String(p.nombre || "")
        .toLowerCase()
        .includes(q)
    )
      return false;
    // filtro "solo sin impuesto"
    if (soloSinImpuesto.value) {
      const tieneIds = Array.isArray(p.taxIds)
        ? p.taxIds.length > 0
        : !!p.taxId;
      if (tieneIds) return false;
    }
    return true;
  });
});

// ====== Acciones single / Modal
const modalVisible = ref(false);
const editado = ref({});
const editFile = ref(null);
const variantesActuales = ref([]);
const variantesEdicion = ref([]);
const galeriaActual = ref([]);
const nuevosArchivosGaleria = ref([]);

// Función para cargar variantes de un producto
const cargarVariantes = async (productoId) => {
  const snap = await getDocs(collection(db, `productos/${productoId}/variantes`));
  variantesActuales.value = snap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
  variantesEdicion.value = variantesActuales.value.map(v => ({ ...v }));
};

// Abrir modal de edición y cargar variantes
const editarProducto = async (prod) => {
  await cargarImpuestos(); // 👈 Asegura que los impuestos estén listos
  await cargarVariantes(prod.id); // 👈 Carga las variantes si tiene
  editado.value = { ...prod };
  // Normaliza atributo en variantes cargadas (legacy)
  variantesEdicion.value = variantesEdicion.value.map(v => ({
    ...v,
    atributo: v.atributo || prod.atributoPrincipal || (Array.isArray(prod.atributos) ? prod.atributos[0] : "") || ""
  }));
  galeriaActual.value = Array.isArray(prod.galeriaEcom) ? [...prod.galeriaEcom] : [];
  modalVisible.value = true;
};


const onEditFileChange = (e) => {
  editFile.value = e.target.files?.[0] || null;
};
const onEditGaleriaChange = (e) => {
  nuevosArchivosGaleria.value = Array.from(e.target.files || []);
};
const eliminarFotoGaleria = (idx) => {
  galeriaActual.value.splice(idx, 1);
};
const agregarVarianteFila = () => {
  variantesEdicion.value.push({ atributo: "", valor: "", descripcion: "", precio: null });
};
const eliminarVariante = (idx) => {
  variantesEdicion.value.splice(idx, 1);
  // Si tiene id, se eliminará al guardar
};
const guardarVariantes = async () => {
  if (!editado.value.id) return;
  const refVar = collection(db, `productos/${editado.value.id}/variantes`);
  // Eliminar todas y re-crear (simplifica). Se podría optimizar con batch.
  const snap = await getDocs(refVar);
  const batchDeletes = [];
  snap.forEach(d => batchDeletes.push(deleteDoc(doc(db, `productos/${editado.value.id}/variantes`, d.id))));
  await Promise.all(batchDeletes);
  const addOps = [];
  const attrsUnique = new Set();
  for (const v of variantesEdicion.value) {
    if (!v.valor?.trim()) continue;
    const attr = v.atributo?.trim() || editado.value.atributoPrincipal || editado.value.atributos?.[0] || "Atributo";
    attrsUnique.add(attr);
    addOps.push(addDoc(refVar, {
      valor: v.valor.trim(),
      atributo: attr,
      descripcion: v.descripcion || "",
      precio: v.precio !== null && v.precio !== undefined ? Number(v.precio) : null,
      actualizado: new Date()
    }));
  }
  await Promise.all(addOps);
  editado.value.atributos = Array.from(attrsUnique);
  editado.value.atributoPrincipal = editado.value.atributos[0] || editado.value.atributoPrincipal || "";
  await cargarVariantes(editado.value.id);
  alert("Variantes actualizadas");
};

const guardarCambios = async () => {
  const refProd = doc(db, "productos", editado.value.id);
  let imagenUrl = editado.value.imagenUrl || "";
  let galeriaUrls = Array.isArray(galeriaActual.value) ? [...galeriaActual.value] : [];
  // atributos desde variantes
  const atributosLista = editado.value.usaVariantes
    ? [...new Set(variantesEdicion.value.map(v => (v.atributo || "").trim()).filter(Boolean))]
    : [];
  const atributoPrincipal = atributosLista[0] || editado.value.atributoPrincipal || "";

  // Subir nueva imagen si corresponde
  if (editFile.value) {
    const path =
      editado.value.imagePath || `productos/${editado.value.id}/main.jpg`;
    const imgRef = sRef(storage, path);
    await uploadBytes(imgRef, editFile.value, {
      contentType: editFile.value.type,
    });
    imagenUrl = await getDownloadURL(imgRef);
    await updateDoc(refProd, { imagePath: path });
  }

  // Subir nueva galería si corresponde
  if (nuevosArchivosGaleria.value.length) {
    // Conservar las existentes y agregar nuevas
    galeriaUrls = Array.isArray(galeriaActual.value) ? [...galeriaActual.value] : [];
    const offset = galeriaUrls.length;
    for (const [i, img] of nuevosArchivosGaleria.value.entries()) {
      const path = `productos/${editado.value.id}/galeria/${offset + i}-${img.name}`;
      const gRef = sRef(storage, path);
      await uploadBytes(gRef, img);
      galeriaUrls.push(await getDownloadURL(gRef));
    }
  }

  const costo = Number(editado.value.costo ?? 0);
  const margen = Number(editado.value.margen ?? 0);
  const precio = costo + (costo * margen) / 100;

  const payload = {
    nombre: editado.value.nombre ?? "",
    categoria: editado.value.categoria ?? "",
    tipo: editado.value.tipo ?? "producto",
    costo,
    margen,
    precio,
    stock: Number(editado.value.stock ?? 0),
    stockMinimo: Number(editado.value.stockMinimo ?? 0),
    taxId: editado.value.taxId || "",
    mostrarEn: editado.value.mostrarEn || "ambos",
    mostrarPOS: editado.value.mostrarPOS ?? undefined, // conservar si lo traía
    mostrarWeb: editado.value.mostrarWeb ?? undefined, // conservar si lo traía
    visible: !!editado.value.visible,
    atributos: atributosLista,
    atributoPrincipal: atributoPrincipal,
    duracion:
      editado.value.tipo === "servicio"
        ? Number(editado.value.duracion ?? 0)
        : 0,
    activo: editado.value.activo ?? true,
    ...(imagenUrl ? { imagenUrl } : {}),
    ...(galeriaUrls.length ? { galeriaEcom: galeriaUrls } : {}),
  };

  await updateDoc(refProd, payload);
  modalVisible.value = false;
  editFile.value = null;
  nuevosArchivosGaleria.value = [];
  await cargarProductos();
};

// ====== Toggles simples
const toggleCampo = async (id, campo, valor) => {
  const prod = productos.value.find((p) => p.id === id);
  if (!prod) return;

  await updateDoc(doc(db, "productos", id), { [campo]: valor });

  let mostrarPOS = campo === "mostrarPOS" ? valor : prod.mostrarPOS;
  let mostrarWeb = campo === "mostrarWeb" ? valor : prod.mostrarWeb;

  let mostrarEn = "ambos";
  if (mostrarPOS && !mostrarWeb) mostrarEn = "pos";
  else if (!mostrarPOS && mostrarWeb) mostrarEn = "ecommerce";
  else if (!mostrarPOS && !mostrarWeb) mostrarEn = "ninguno";

  await updateDoc(doc(db, "productos", id), { mostrarEn });

  prod[campo] = valor;
  prod.mostrarEn = mostrarEn;
};

// ====== Eliminar
const eliminarProducto = async (id) => {
  if (!confirm("¿Eliminar este producto permanentemente?")) return;
  await deleteDoc(doc(db, "productos", id));
  await cargarProductos();
};

// ====== Acción MASIVA: aplicar o quitar impuesto en productos (no servicios)
const aplicarImpuestoMasivo = async (aplicar) => {
  if (aplicar && !selectedTaxId.value) {
    alert("Selecciona un impuesto primero.");
    return;
  }

  // Construir candidatos (excluye servicios SIEMPRE)
  let candidates = productos.value.filter(
    (p) => String(p.tipo).toLowerCase() !== "servicio"
  );

  if (filtroCategoria.value) {
    candidates = candidates.filter(
      (p) => p.categoria === filtroCategoria.value
    );
  }
  if (buscar.value.trim()) {
    const q = buscar.value.trim().toLowerCase();
    candidates = candidates.filter((p) =>
      String(p.nombre || "")
        .toLowerCase()
        .includes(q)
    );
  }
  if (soloSinImpuesto.value) {
    candidates = candidates.filter((p) => {
      const tieneIds = Array.isArray(p.taxIds)
        ? p.taxIds.length > 0
        : !!p.taxId;
      return !tieneIds;
    });
  }

  if (!candidates.length) {
    alert("No hay productos que coincidan con el filtro.");
    return;
  }

  const actionText = aplicar
    ? `APLICAR impuesto ${selectedTaxId.value}`
    : "QUITAR impuesto";
  if (
    !confirm(
      `Vas a ${actionText} a ${candidates.length} producto(s). ¿Continuar?`
    )
  )
    return;

  loading.value = true;
  progreso.value = { current: 0, total: candidates.length, lote: 0 };

  const chunkSize = 450;
  const chunks = Math.ceil(candidates.length / chunkSize);

  try {
    for (let c = 0; c < chunks; c++) {
      progreso.value.lote = c + 1;
      const batch = writeBatch(db);
      const slice = candidates.slice(c * chunkSize, (c + 1) * chunkSize);

      for (const p of slice) {
        const ref = doc(db, "productos", p.id);
        if (aplicar) {
          // Aquí usamos taxId simple; si quieres múltiples, cambia a taxIds: [selectedTaxId.value]
          batch.update(ref, { taxId: selectedTaxId.value, taxIds: null });
        } else {
          batch.update(ref, { taxId: null, taxIds: null });
        }
        progreso.value.current++;
      }

      await batch.commit();
    }

    alert(" Cambios masivos aplicados");
  } catch (err) {
    console.error(" Error en acción masiva:", err);
    alert("Ocurrió un error aplicando los cambios masivos. Revisa consola.");
  } finally {
    loading.value = false;
    await cargarProductos();
  }
};
</script>
